import clsx from "clsx";
import { FC, ReactNode, Suspense, useEffect, useState } from "react";
import { Icons, Toggle, useClickOutside, useTranslation } from "rujira.ui";
import { ContextWrapper } from "../../ContextWrapper";
import { useIsBelow, useIsMobile } from "../../hooks/useBreakpoint";
import { useScrollEdges } from "../../hooks/useScrollEdges";
import { TRADE_AUTO_CLAIM_ENABLED } from "../features";
import { TradeBreakpoint } from "../static";
import { AutoClaimerStatusMenu } from "./AutoClaimerStatusMenu";
import { OrderRowsFallback } from "./mobile/OrderRow";
import { OrdersSettings } from "./mobile/OrdersSettings";
import { Tab, TabsOverflowContext, useOrdersContext } from "./Orders/Context";
import {
  BulkActions as LimitBulkActions,
  Content as LimitContent,
  Context as LimitContext,
  TrackingContext,
  Table as LimitTable,
  TableFallback as LimitTableFallback,
  Tabs as LimitTabs,
  useLimitOrderCategoriesData,
} from "./Orders/Limit";
import {
  Content as RangeContent,
  Context as RangeContext,
  Table as RangeTable,
  Tabs as RangeTabs,
  useRangeOrdersData,
} from "./Orders/Range";
import {
  Content as RecurringContent,
  Context as RecurringContext,
  Table as RecurringTable,
  Tabs as RecurringTabs,
  useRecurringOrdersData,
} from "./Orders/Recurring";

const useOrderData = () => {
  const { limit, tracking } = useLimitOrderCategoriesData();
  const recurring = useRecurringOrdersData();
  const range = useRangeOrdersData();
  return { limit, tracking, recurring, range };
};

type OrdersData = ReturnType<typeof useOrderData>;

const useOrderLabels = () => {
  const { t } = useTranslation();
  return {
    [Tab.Limit]: t("limit"),
    [Tab.Tracking]: t("tracking"),
    [Tab.Recurring]: t("recurring"),
    [Tab.Automated]: t("automated"),
  };
};

export const Orders: FC = () => {
  return (
    <ContextWrapper
      contexts={[
        LimitContext,
        TrackingContext,
        RecurringContext,
        RangeContext,
      ]}>
      <Suspense fallback={<OrdersFallback />}>
        <OrdersInner />
      </Suspense>
    </ContextWrapper>
  );
};

const TABS = [Tab.Limit, Tab.Tracking, Tab.Recurring, Tab.Automated];

const TabScroller: FC<{ children: ReactNode }> = ({ children }) => {
  const { ref, edges, overflows } = useScrollEdges<HTMLSpanElement>();

  return (
    <span
      ref={ref}
      className={clsx("tabs tabs--sm orders-header__subtabs", {
        "orders-header__subtabs--start": edges.start,
        "orders-header__subtabs--end": edges.end,
      })}>
      <TabsOverflowContext.Provider value={overflows}>
        {children}
      </TabsOverflowContext.Provider>
    </span>
  );
};

export const OrdersFallback: FC = () => {
  const labels = useOrderLabels();
  const { tab } = useOrdersContext();
  const rows = useIsBelow(TradeBreakpoint);
  const typeTab = (
    <a className="selected w-16 flex jc-sb">
      <p className="color-white ml-1">{labels[tab]}</p>
      <Icons.AngleDown className="color-white" />
      {!rows && (
        <nav>
          {TABS.filter((a) => a !== tab).map((a) => (
            <span
              key={a}
              className="flex ai-c"
              style={{
                fontSize: "0.75rem",
              }}>
              <label className="ml-1">{labels[a]}</label>
            </span>
          ))}
        </nav>
      )}
    </a>
  );

  return (
    <>
      <div className="orders-header flex ai-c gap-2">
        {rows ? (
          <>
            <TabScroller>
              {typeTab}
              <SubTabsFallback rows />
            </TabScroller>
            <div className="orders-header__actions">
              {tab === Tab.Automated && <RangeTabs />}
            </div>
          </>
        ) : (
          <nav className="tabs tabs--sm">
            {typeTab}
            <div className="divider mx-1" />
            <TabScroller>
              <SubTabsFallback />
            </TabScroller>
          </nav>
        )}
      </div>
      {rows ? (
        <div className="trade__orders-table mt-2">
          <OrderRowsFallback />
        </div>
      ) : (
        <LimitTableFallback />
      )}
    </>
  );
};

export const OrdersInner: FC = () => {
  const { t } = useTranslation();
  const labels = useOrderLabels();
  const { only, setOnly, tab, selectTab } = useOrdersContext();
  const rows = useIsBelow(TradeBreakpoint);
  const data = useOrderData();
  const { limit, tracking, recurring, range } = data;
  const totalByTab: Record<Tab, number | undefined> = {
    [Tab.Limit]: limit.total,
    [Tab.Tracking]: tracking.total,
    [Tab.Recurring]: recurring.total,
    [Tab.Automated]: range.open.length,
  };
  const count = totalByTab[tab];
  const others = TABS.filter((a) => a !== tab);

  const [typeOpen, setTypeOpen] = useState(false);
  const headerRef = useClickOutside(() => setTypeOpen(false));
  // The menu only renders in row mode; don't let it reopen on the next resize.
  useEffect(() => {
    setTypeOpen(false);
  }, [rows]);

  const typeTab = (
    <a
      className="selected flex jc-sb pointer"
      aria-expanded={rows ? typeOpen : undefined}
      onClick={rows ? () => setTypeOpen((v) => !v) : undefined}>
      <p className="color-white ml-1">{labels[tab]}</p>
      {count ? <i>{count || undefined}</i> : null}

      <Icons.AngleDown className="color-white" />
      {!rows && (
        <nav>
          {others.map((a) => (
            <span
              key={a}
              onClick={() => selectTab(a)}
              className="flex ai-c"
              style={{
                fontSize: "0.75rem",
              }}>
              <label className="ml-1">{labels[a]}</label>
              {totalByTab[a] ? <i>{totalByTab[a]}</i> : null}
            </span>
          ))}
        </nav>
      )}
    </a>
  );

  return (
    <>
      <div className="orders-header flex ai-c gap-2" ref={headerRef}>
        {rows ? (
          <TabScroller>
            {typeTab}
            <SubTabs rows data={data} />
          </TabScroller>
        ) : (
          <nav className="tabs tabs--sm">
            {typeTab}
            <div className="divider mx-1" />
            <TabScroller>
              <SubTabs data={data} />
            </TabScroller>
          </nav>
        )}
        {rows && typeOpen && (
          <nav className="tabs tabs--sm orders-header__type-menu">
            {others.map((a) => (
              <a
                key={a}
                className="pointer"
                onClick={() => {
                  selectTab(a);
                  setTypeOpen(false);
                }}>
                {labels[a]}
                {totalByTab[a] ? <i>{totalByTab[a]}</i> : null}
              </a>
            ))}
          </nav>
        )}
        {rows ? (
          <div className="orders-header__actions">
            <SubActions data={data} />
            <OrdersSettings only={only} setOnly={setOnly} />
          </div>
        ) : (
          <div className="orders-header__controls flex ai-c gap-0.5">
            {TRADE_AUTO_CLAIM_ENABLED && <AutoClaimerStatusMenu />}
            <Toggle
              className="toggle--xs as-c"
              checked={only}
              onChange={() => setOnly(!only)}
              label={t("showThisPairOnly")}
            />
          </div>
        )}
      </div>
      <Content data={data} />
    </>
  );
};

const SubTabs: FC<{ rows?: boolean; data: OrdersData }> = ({
  rows,
  data: { limit, tracking, recurring },
}) => {
  const { tab } = useOrdersContext();

  switch (tab) {
    case Tab.Limit:
      return <LimitTabs data={limit} />;
    case Tab.Tracking:
      return <LimitTabs data={tracking} category="tracking" />;
    case Tab.Recurring:
      return <RecurringTabs data={recurring} />;
    case Tab.Automated:
      return rows ? null : <RangeTabs />;
  }
};

const SubActions: FC<{ data: OrdersData }> = ({ data }) => {
  const { tab } = useOrdersContext();

  switch (tab) {
    case Tab.Limit:
      return <LimitBulkActions data={data.limit} />;
    case Tab.Automated:
      return <RangeTabs />;
    default:
      return null;
  }
};

const SubTabsFallback: FC<{ rows?: boolean }> = ({ rows }) => {
  const { tab } = useOrdersContext();

  switch (tab) {
    case Tab.Limit:
      return <LimitTabs />;
    case Tab.Tracking:
      return <LimitTabs category="tracking" />;
    case Tab.Recurring:
      return <RecurringTabs />;
    case Tab.Automated:
      return rows ? null : <RangeTabs />;
  }
};

const Content: FC<{ data: OrdersData }> = ({ data }) => {
  const { tab } = useOrdersContext();

  switch (tab) {
    case Tab.Limit:
      return <LimitContent data={data.limit} />;
    case Tab.Tracking:
      return <LimitContent category="tracking" data={data.tracking} />;
    case Tab.Recurring:
      return <RecurringContent data={data.recurring} />;
    case Tab.Automated:
      return <RangeContent data={data.range} />;
  }
};

export const blockSubscriptionId = btoa("ThorchainBlock:latest");

const TabC: FC<{
  tab: Tab;
  setTab: (t: Tab) => void;
  selected: Tab;
  count: number;
}> = ({ tab, setTab, selected, count }) => {
  const labels = useOrderLabels();
  return (
    <a
      className={clsx({ selected: selected === tab })}
      onClick={() => setTab(tab)}>
      <label>{labels[tab]}</label>
      {count ? <i>{count}</i> : null}
    </a>
  );
};

enum RangeView {
  Open = "open",
  Closed = "closed",
}

export const OrdersPortfolio: FC = () => {
  const data = useOrderData();
  const { limit, tracking, recurring, range } = data;

  const [tab, setTab] = useState<Tab>(Tab.Limit);
  const [rangeView, setRangeView] = useState<RangeView>(RangeView.Open);
  const isMobile = useIsMobile();
  const counts = {
    [Tab.Limit]: limit.total,
    [Tab.Tracking]: tracking.total,
    [Tab.Recurring]: recurring.total,
    [Tab.Automated]: range.open.length,
  };
  const rangeToggle = (
    <Toggle
      className={clsx("toggle--xs as-c", !isMobile && "ml-a mr-1.5")}
      labelOff="Closed"
      label="Open"
      checked={rangeView === RangeView.Open}
      onChange={(e) =>
        setRangeView(e.target.checked ? RangeView.Open : RangeView.Closed)
      }
    />
  );

  return (
    <RangeContext>
      <div
        className={clsx(
          "tabs tabs--sm py-1",
          isMobile &&
            tab === Tab.Automated &&
            "orders-portfolio-tabs--automated-mobile"
        )}>
        {isMobile ? (
          <OrdersDropdown tab={tab} setTab={setTab} counts={counts} />
        ) : (
          TABS.map((item) => (
            <TabC
              key={item}
              tab={item}
              setTab={setTab}
              selected={tab}
              count={counts[item]}
            />
          ))
        )}
        {tab === Tab.Automated &&
          (isMobile ? (
            <span className="orders-portfolio-tabs__automated-controls flex ai-c jc-sb">
              <RangeTabs compact />
              {rangeToggle}
            </span>
          ) : (
            <>
              <div className="divider mx-1" />
              <RangeTabs />
              {rangeToggle}
            </>
          ))}
      </div>
      <div className="pl-1">
        <PortfolioContent tab={tab} rangeView={rangeView} data={data} />
      </div>
    </RangeContext>
  );
};

const OrdersDropdown: FC<{
  tab: Tab;
  setTab?: (tab: Tab) => void;
  counts?: Partial<Record<Tab, number>>;
}> = ({ tab, setTab, counts = {} }) => {
  const labels = useOrderLabels();

  return (
    <a className="selected flex jc-sb">
      <p className="color-white ml-1">{labels[tab]}</p>
      {counts[tab] ? <i>{counts[tab]}</i> : null}
      <Icons.AngleDown className="color-white" />
      <nav>
        {TABS.filter((item) => item !== tab).map((item) => (
          <span
            key={item}
            className="flex ai-c"
            style={{ fontSize: "0.75rem" }}
            onClick={() => setTab?.(item)}>
            <label className="ml-1">{labels[item]}</label>
            {counts[item] ? <i>{counts[item]}</i> : null}
          </span>
        ))}
      </nav>
    </a>
  );
};

const PortfolioContent: FC<{
  tab: Tab;
  rangeView: RangeView;
  data: OrdersData;
}> = ({ tab, rangeView, data }) => {
  const { limit, tracking, recurring, range } = data;

  switch (tab) {
    case Tab.Limit:
      return <LimitTable orders={limit.all} />;
    case Tab.Tracking:
      return <LimitTable orders={tracking.all} category="tracking" />;
    case Tab.Recurring:
      return (
        <RecurringTable
          orders={[
            ...recurring.active,
            ...recurring.paused,
            ...recurring.completed,
          ]}
          expanded
        />
      );
    case Tab.Automated:
      return (
        <RangeTable
          orders={rangeView === RangeView.Open ? range.open : range.closed}
        />
      );
  }
};
